import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getAuthHeaders } from '../../utils/authHeaders'

const API_URL = process.env.REACT_APP_BACKEND_URL

const GradeScenarios = () => {
  const navigate = useNavigate()
  const { grade } = useParams()
  const gradeNumber = parseInt(grade)
  
  const [scenarios, setScenarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Emoji mapping based on slug
  const emojiMap = {
    'money-concept': '💰',
    'needs-wants': '🛍️',
    'school-buffet': '🏫',
    'mobile': '📱'
  }

  useEffect(() => {
    fetchScenarios()
  }, [gradeNumber])

  const fetchScenarios = async () => {
    try {
      setLoading(true)
      const headers = await getAuthHeaders()
      const response = await fetch(
        `${API_URL}/api/admin/public/financial-scenarios?grade=${gradeNumber}`,
        { headers }
      )
      
      if (!response.ok) {
        throw new Error('Failed to load scenarios')
      }
      
      const data = await response.json()
      
      // Map DB data to display format
      const mappedScenarios = data.map(s => ({
        id: s.slug,
        title: s.title,
        emoji: emojiMap[s.slug] || '📚',
        description: s.description || '',
        duration: `${s.estimated_duration_minutes} minút`,
        steps: 10, // Default placeholder (would need to be in DB for accuracy)
        topics: s.topics_text ? s.topics_text.split(',').map(t => t.trim()) : []
      }))
      
      setScenarios(mappedScenarios)
      setError('')
    } catch (err) {
      console.error('Error loading scenarios:', err)
      setError('Nepodarilo sa načítať scenáre')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítavam scenáre...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchScenarios}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Skúsiť znova
          </button>
        </div>
      </div>
    )
  }

  if (scenarios.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-green-100 mb-1">{gradeNumber}. ročník</div>
                  <h1 className="text-3xl font-bold">Interaktívne scenáre</h1>
                </div>
                <button
                  onClick={() => navigate('/scenarios')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť na ročníky
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-600">Pre tento ročník momentálne nie sú dostupné žiadne scenáre.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-green-100 mb-1">{gradeNumber}. ročník</div>
                <h1 className="text-3xl font-bold">Interaktívne scenáre</h1>
              </div>
              <button
                onClick={() => navigate('/scenarios')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
              >
                ← Späť na ročníky
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-8">
            <p className="text-gray-700 text-sm">
              <strong>💡 Tip:</strong> Scenáre môžete používať individuálne so žiakmi alebo ako skupinovú aktivitu. Žiadne odpovede nie sú "nesprávne" – dôležité je diskutovať o rôznych riešeniach.
            </p>
          </div>

          <div className="space-y-6">
            {scenarios.map((scenario) => (
              <div
                key={scenario.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8"
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-green-100 to-blue-100 rounded-xl flex items-center justify-center text-5xl">
                    {scenario.emoji}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <h2 className="text-2xl font-bold text-gray-900">{scenario.title}</h2>
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        {scenario.steps} krokov
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {scenario.description}
                    </p>
                    
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        {scenario.duration}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {scenario.topics.map((topic, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                    
                    <button
                      onClick={() => navigate(`/scenarios/${scenario.id}`)}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                    >
                      Začať scenár →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

    </div>
  )
}

export default GradeScenarios
